import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';

const AnimatedText = ({ text, className = "" }) => (
    <div className={`${className}`}>
        {text}
    </div>
);

const WriteSection = ({ isDarkMode = true }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (isExpanded) {
            setIsAnimating(true);
        }
    }, [isExpanded]);

    const handleToggle = (expand) => {
        if (expand) {
            setIsExpanded(true);
        } else {
            setIsAnimating(false);
            setTimeout(() => {
                setIsExpanded(false);
            }, 300);
        }
    };

    const articles = [
        {
            title: "How Apple beat Swiss watchmakers at their own game",
            icon: (
                <svg width="64" height="64" viewBox="0 0 64 64" stroke="currentColor"
                     className="transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 group-hover:stroke-blue-500"
                     fill="none" strokeWidth="1">
                    <path d="M32 16L16 32L32 48L48 32L32 16" />
                    <circle cx="32" cy="32" r="1.5" fill="currentColor" className="group-hover:fill-blue-500" />
                </svg>
            )
        },
        {
            title: "My chatbot is dead",
            icon: (
                <svg width="64" height="64" viewBox="0 0 64 64" stroke="currentColor"
                     className="transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 group-hover:stroke-purple-500"
                     fill="none" strokeWidth="1">
                    <rect x="20" y="20" width="24" height="24" />
                    <rect x="26" y="26" width="12" height="12" className="transition-all duration-500 group-hover:fill-purple-500" />
                </svg>
            )
        },
        {
            title: "The rise of Anti-notifications",
            icon: (
                <svg width="64" height="64" viewBox="0 0 64 64" stroke="currentColor"
                     className="transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 group-hover:stroke-pink-500"
                     fill="none" strokeWidth="1">
                    <rect x="20" y="20" width="24" height="24" />
                    <circle cx="38" cy="26" r="6" className="transition-all duration-500 group-hover:fill-pink-500" />
                </svg>
            )
        }
    ];

    const projects = [
        {
            title: "The Power of Unfinished",
            duration: "10 min",
            subtitle: "How to get more done, uplevel your craft"
        },
        {
            title: "Framer is dead",
            duration: "8 min",
            subtitle: "A love letter to my design tool of choice"
        },
        {
            title: "Meaningful motion in design",
            duration: "10 min",
            subtitle: "The small details that make up great interfaces"
        },
        {
            title: "The loss of micro-privacy",
            duration: "18 min",
            subtitle: "How small design changes re-wrote human interaction"
        }
    ];

    return (
        <div className="mt-12 sm:mt-16 md:mt-24 lg:mt-32 px-4 sm:px-6 md:px-2 lg:px-32">
            {/* Header Section */}
            <div>
                <div className="max-w-3xl mb-12 sm:mb-16">
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-mono tracking-tight mb-8">
                        <AnimatedText text="Write" />
                    </h2>
                    <p className="font-mono text-sm sm:text-base text-gray-400 leading-relaxed">
                        Writing is an essential part of my process. My essays were featured on FastCompany, TNW, Smashing Magazine, and Medium. You can find some of my latest writing below.
                    </p>
                </div>
            </div>

            {/* Mobile Horizontal Scroll */}
            <div className="sm:hidden overflow-x-auto overflow-y-hidden scrollbar-hide">
                <div className="flex gap-2 pb-4">
                    {articles.map((article, index) => (
                        <div key={index} style={{ width: 'calc(100vw / 1.8)' }} className="flex flex-col gap-4 flex-shrink-0">
                            <div className={`aspect-[3/2] rounded-3xl
                                          group transition-all duration-500 ease-out 
                                          flex items-center justify-center cursor-pointer
                                          overflow-hidden transform-gpu hover:-translate-y-1
                                          ${isDarkMode ? 'bg-[#111111] hover:bg-[#1a2030]' : 'bg-gray-100 hover:bg-gray-50 shadow-sm hover:shadow-md'}`}>
                                {article.icon}
                            </div>
                            <h3 className="font-mono text-sm tracking-tight text-left pl-2">
                                <AnimatedText text={article.title} />
                            </h3>
                        </div>
                    ))}
                </div>
            </div>

            {/* Desktop and Tablet Grid */}
            <div className="hidden sm:block">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-3 lg:gap-8">
                    {articles.map((article, index) => (
                        <div key={index} className="flex flex-col gap-4">
                            <div className={`aspect-[5/3] sm:aspect-[3/2] md:aspect-[5/3] rounded-3xl
                                          group transition-all duration-500 ease-out
                                          flex items-center justify-center cursor-pointer
                                          transform-gpu will-change-transform hover:-translate-y-1
                                          ${isDarkMode ? 'bg-[#111111] hover:bg-[#1a2030]' : 'bg-gray-100 hover:bg-gray-50 shadow-sm hover:shadow-md'}`}>
                                <div className="transform transition-transform duration-500 ease-out">
                                    {article.icon}
                                </div>
                            </div>
                            <h3 className="font-mono text-sm sm:text-base tracking-tight text-left pl-2">
                                <AnimatedText text={article.title} />
                            </h3>
                        </div>
                    ))}
                </div>
            </div>

            {/* Projects Section */}
            <div className="mt-16">
                <div className="flex flex-col items-center">
                    {!isExpanded && (
                        <button
                            onClick={() => handleToggle(true)}
                            className={`w-16 h-16 rounded-full flex items-center justify-center
                                     transition-all duration-300 ease-in-out hover:scale-105
                                     ${isDarkMode ? 'bg-[#111111] hover:bg-[#1a2030]' : 'bg-gray-100 hover:bg-gray-50 shadow-sm hover:shadow-md'}`}
                            aria-label="Show projects"
                        >
                            <Plus className="w-8 h-8" />
                        </button>
                    )}

                    {isExpanded && (
                        <div className="w-full transition-all duration-300 ease-in-out">
                            <div className="space-y-8">
                                {projects.map((project, index) => (
                                    <div key={index}
                                         className="cursor-pointer hover:opacity-80 transition-opacity duration-300"
                                         style={{
                                             transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                                             opacity: isAnimating ? 1 : 0,
                                             transform: isAnimating ? 'translateY(0)' : 'translateY(20px)',
                                             transitionDelay: isAnimating ? `${index * 40}ms` : `${(projects.length - index - 1) * 40}ms`
                                         }}>
                                        <div className="flex flex-start gap-2">
                                            <h3 className={`font-mono text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                {project.title}
                                            </h3>
                                            <span className={`font-mono text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                                                ({project.duration})
                                            </span>
                                        </div>
                                        <p className={`font-mono text-sm mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                                            {project.subtitle}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {isExpanded && (
                        <button
                            onClick={() => handleToggle(false)}
                            className={`mt-12 w-16 h-16 rounded-full flex items-center justify-center
                                     transition-all duration-300 ease-in-out hover:scale-105
                                     ${isDarkMode ? 'bg-[#111111] hover:bg-[#1a2030]' : 'bg-gray-100 hover:bg-gray-50 shadow-sm hover:shadow-md'}`}
                            aria-label="Close projects"
                        >
                            <X className="w-8 h-8" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WriteSection;